/**
 * ClientApisProvider — how shared hooks get the app's API functions.
 *
 * WHY THIS EXISTS
 *
 * App code imports an API action directly from the app's own binding file
 * (`import { createChannel } from '@apis/comms'`), and that stays the rule for
 * app code. A hook that lives in `shared/` cannot do that: shared code knows
 * nothing about `frontend/` or `mobile/`, and it cannot build the API itself
 * because `createCommsApi(authedFetch)` needs the app's transport (a cookie on
 * the web, a Bearer sid on mobile).
 *
 * So the direction is reversed: instead of the hook reaching out for the API,
 * the app hands the API in. This is dependency injection, done the way React
 * does it — through a Context. Each app builds its API objects once at
 * startup, puts them in this provider at the root, and every shared hook reads
 * them with `useClientApis()`. The hook's recipe stays identical in both apps;
 * only the transport underneath differs, and the hook never sees it.
 *
 *   authedFetch (app) → createCommsApi(authedFetch) (shared factory)
 *     → commsApi (app binding, apis/comms.ts) → <ClientApisProvider apis={…}>
 *       → useClientApis().comms.createChannel (shared hook)
 *
 * WHAT GOES IN
 *
 * `IClientApis` lists the bound API objects shared hooks may use. Add a field
 * when a hook that needs it moves into shared; nothing is registered
 * speculatively. The app binding file already builds each object — it just
 * exports it and the app's `clientApis.ts` collects them.
 *
 * PERFORMANCE
 *
 * A Context re-renders its consumers only when the provider's `value` gets a
 * new identity. The API objects are built once at module load and never
 * replaced, so this provider never triggers a render — it behaves like a
 * global reachable through React, exactly as `QueryClientProvider` and the
 * redux `Provider` do. Keep it that way: build `apis` once outside render
 * (`export const clientApis = { comms: commsApi }`), never inline in JSX.
 *
 * TESTS
 *
 * A shared hook's test does not mock modules; it renders inside this provider
 * with a stub in place of the real API (`apis={{ comms: { createChannel: fn } }}`)
 * and asserts on the stub. Same wrapper shape for every hook.
 *
 * Written without JSX so the shared package's own `.ts`-only tsconfig covers it.
 */
import { createContext, createElement, useContext, type ReactNode } from 'react';

import type { createCommsApi } from '../apis/comms.js';
import type { createNotificationsApi } from '../apis/notifications.js';
import type { createIssuesApi } from '../apis/issues.js';
import type { createStorageApi } from '../apis/storage.js';

/** The bound API objects an app makes available to shared hooks. */
export interface IClientApis {
  comms: ReturnType<typeof createCommsApi>;
  notifications: ReturnType<typeof createNotificationsApi>;
  issues: ReturnType<typeof createIssuesApi>;
  storage: ReturnType<typeof createStorageApi>;
}

export interface IClientApisProviderProps {
  /** Built once at startup; must keep the same identity for the app's lifetime. */
  apis: IClientApis;
  children?: ReactNode;
}

const ClientApisContext = createContext<IClientApis | null>(null);

/** Mount once at the app root, next to `QueryClientProvider`. */
export function ClientApisProvider({ apis, children }: IClientApisProviderProps) {
  return createElement(ClientApisContext.Provider, { value: apis }, children);
}

/** Read the app's APIs from inside a shared hook. */
export function useClientApis(): IClientApis {
  const apis = useContext(ClientApisContext);
  if (!apis) throw new Error('useClientApis: no ClientApisProvider above this component');
  return apis;
}
